import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from './EvaluationPagination.module.scss';
import {ReactComponent as LeftIcon} from './ArrowLeft.svg';
import {ReactComponent as RightIcon} from './ArrowRight.svg';
import PropTypes from "prop-types";
import useDeviceType from "../../hooks/useDeviceType";
import useClickOutside from "../../hooks/useClickOutside";

const EvaluationPagination = ({
  currentQuestion,
  totalQuestions = 20,
  attendedQuestion = [],
  onChange,
}) => {
  const [visibleQuestions, setVisibleQuestions] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isMobile, isLargeDevice } = useDeviceType();
  const activeButtonRef = useRef(null);
  const dropdownRef = useRef(null);

  useClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  const updateVisibleQuestions = useCallback((questionNumber) => {
    const siblingsLimit = isMobile ? 0 : isLargeDevice ? 2 : 1;
    const visibleLimit = siblingsLimit * 2 + 1;
    const start = Math.max(1, Math.min(questionNumber - siblingsLimit, totalQuestions - visibleLimit + 1));
    const end = Math.min(Math.max(questionNumber + siblingsLimit, visibleLimit), totalQuestions);
    setVisibleQuestions(Array.from({ length: end - start + 1 }, (_, index) => start + index));
  }, [totalQuestions, isMobile, isLargeDevice]);

  useEffect(() => {
    updateVisibleQuestions(currentQuestion);
  }, [currentQuestion, updateVisibleQuestions]);

  useEffect(() => {
    if(isMobile && isDropdownOpen) {
      activeButtonRef.current.scrollIntoView({
        behavior: 'instant',
        block: 'center', 
      });
    }
  }, [isDropdownOpen, isMobile]);

  return (
    <div className={styles.pagination}>
      <button
        className={currentQuestion === 1 ? styles.disabled : ''}
        onClick={() => {
          updateVisibleQuestions(currentQuestion - 1);
          onChange(currentQuestion - 1);
        }}
        disabled={currentQuestion === 1}
      >
        <LeftIcon />Previous
      </button>
      
      {visibleQuestions.map((questionNumber) => (
        <button
          key={questionNumber}
          className={`${questionNumber === currentQuestion ? styles.active : ''}
                      ${attendedQuestion.includes(questionNumber) ? styles.submitted : ''}`}
          onClick={() => {
            updateVisibleQuestions(questionNumber);
            if(questionNumber !== currentQuestion) {
              onChange(questionNumber);
            }
            if(isMobile) {
              setIsDropdownOpen(true);
            }
          }}
        >
          {isMobile ? 'Q' : 'Question ' }{questionNumber}
        </button>
      ))}

      {isMobile && isDropdownOpen && <div className={styles.dropdown} ref={dropdownRef}>
        {Array.from({ length: totalQuestions }, (_, index) => {
          const questionNumber = index + 1;
          return (
            <button
              key={questionNumber}
              className={`${questionNumber === currentQuestion ? styles.active : ''}
                          ${attendedQuestion.includes(questionNumber) ? styles.submitted : ''}`}
              onClick={() => {
                onChange(questionNumber);
                setIsDropdownOpen(false);
              }}
              ref={questionNumber === currentQuestion ? activeButtonRef : null}
            >
              Question {questionNumber}
            </button>
          );
        })}
      </div>}

      <button
        className={currentQuestion === totalQuestions ? styles.disabled : ''}
        onClick={() => {
          updateVisibleQuestions(currentQuestion + 1);
          onChange(currentQuestion + 1);
        }}
        disabled={currentQuestion === totalQuestions}
      >
        Next <RightIcon />
      </button>
    </div>
  );
};

EvaluationPagination.propTypes = {
  currentQuestion: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number,
  attendedQuestion: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired
};

export default EvaluationPagination;